import React, { useState, useEffect } from 'react';
import { request } from '../../utils/api';
import { Calendar as CalendarIcon } from 'lucide-react';

const AttendanceCalendar = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCalendar = async () => {
      setLoading(true);
      try {
        const response = await request('/attendance/calendar');
        if (response && response.status === 1) {
          setData(response.result);
        }
      } catch (err) {
        console.error("Error fetching calendar:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCalendar();
  }, []);

  if (loading || !data) {
    return (
      <div className="bg-white rounded-xl p-4 shadow-sm border border-indigo-200 h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-400"></div>
      </div>
    );
  }

  const { days: rawDays, month, year } = data;
  // Only show current month dates (no previous/next month data)
  const days = (rawDays || []).filter((d) => {
    const [y, m] = d.date.split('-').map(Number);
    return y === year && m === month;
  });

  if (days.length === 0) {
    return (
      <div className="bg-white rounded-xl p-4 shadow-sm border border-indigo-200 h-full">
        <h3 className="text-indigo-800 font-semibold text-sm">Attendance Calendar</h3>
        <p className="text-slate-500 text-xs mt-2">No data for this month.</p>
      </div>
    );
  }

  // First day of month: use local date only (no time/timezone conversion)
  const [firstY, firstM, firstD] = days[0].date.split('-').map(Number);
  const firstDayDate = new Date(firstY, firstM - 1, firstD);
  const startDay = firstDayDate.getDay(); // 0-6 (Sun-Sat)

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthName = new Date(year, month - 1, 1).toLocaleString('default', { month: 'long' });

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-indigo-200 h-full">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-4 h-4 text-indigo-500" />
          <h3 className="text-indigo-800 font-semibold text-sm">Attendance Calendar</h3>
        </div>
        <div className="text-xs text-slate-500 font-medium">
          {monthName} {year}
        </div>
      </div>

      <div className="grid grid-cols-7 mb-2">
        {weekDays.map(day => (
          <div key={day} className="text-center text-xs font-semibold text-slate-500 py-1">
            {day}
          </div>
        ))}
      </div>

      {/* Compact Calendar Grid */}
      <div className="grid grid-cols-7 gap-y-1 mb-3">
        {/* Empty slots for days before start of month */}
        {Array.from({ length: startDay }).map((_, i) => (
          <div key={`empty-${i}`} className="h-8"></div>
        ))}

        {days.map((dayObj) => {
          const showHoliday = dayObj.isHoliday === true ;
          // Use America/Los_Angeles time zone to determine "today"
          const nowLA = new Date(
            new Date().toLocaleString("en-US", { timeZone: "America/Los_Angeles" })
          );
          const yyyy = nowLA.getFullYear();
          const mm = String(nowLA.getMonth() + 1).padStart(2, '0');
          const dd = String(nowLA.getDate()).padStart(2, '0');
          const todayLA = `${yyyy}-${mm}-${dd}`;
          const isToday = dayObj.date === todayLA;

          // Determine status based on API structure
          let statusClass = '';
          let statusDot = null;

          if (showHoliday) {
            statusClass = 'bg-purple-50 border-purple-200';
            statusDot = <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>;
          } else if (dayObj.present === true) {
            // Present
            statusClass = 'bg-green-50 border-green-200';
            statusDot = <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>;
          } else if (dayObj.present == null || dayObj.present === false) {
            // Absent
            statusClass = 'bg-red-50 border-red-200';
            statusDot = <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>;
          } 
          
          // if (dayObj.present === null) {
            // Not recorded yet
            // statusClass = showHoliday ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200';
            // if (showHoliday) {
            //   statusDot = <div className="w-1.5 h-1.5 rounded-full bg-gray-400"></div>;
            // }
          // }

          return (
            <div 
              key={dayObj.date} 
              className={`flex flex-col items-center justify-center gap-1 h-8 border rounded transition-colors ${statusClass} ${isToday ? 'ring-1 ring-indigo-400' : ''}`}
            >
              <span className={`text-xs font-medium ${isToday ? 'text-indigo-600 font-bold' : 'text-slate-700'}`}>
                {dayObj.date.split('-')[2]}
              </span>
              {statusDot && (
                <div className="flex items-center justify-center">
                  {statusDot}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Compact Legend */}
      <div className="flex items-center justify-center gap-4 pt-3 border-t border-indigo-100">
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
          <span className="text-xs text-slate-600">Present</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
          <span className="text-xs text-slate-600">Absent</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
          <span className="text-xs text-slate-600">Holiday</span>
        </div>
      </div>
    </div>
  );
};

export default AttendanceCalendar;
